/**
 * Hardcoded demo data for the Credit Card Application Streamlining
 * Experiment. Used only when the experiment ID exactly matches the demo ID
 * — see getExperimentById / getPrototypeByExperiment for the gating. Real
 * experiments with real IDs are unaffected.
 */

import type {
  ExperimentDetail,
  PrototypeDetailResponse,
} from "../api/experimentsApi";
import type { FeedbackFlow } from "../types/experiment.types";

export const DEMO_EXPERIMENT_ID_CREDIT_CARD = "exp-3";
export const DEMO_EXPERIMENT_NAME_CREDIT_CARD =
  "Credit Card Application Streamlining Experiment";

const DEMO_PROTOTYPE_PATH = "/demo/chase-credit-card-application";

const resolveDemoPrototypeUrl = (): string => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${DEMO_PROTOTYPE_PATH}`;
  }
  return DEMO_PROTOTYPE_PATH;
};

export const isDemoCreditCardExperimentId = (
  experimentId?: string | null,
): boolean =>
  (experimentId ?? "").trim() === DEMO_EXPERIMENT_ID_CREDIT_CARD;

export const getCreditCardDemoExperiment = (): ExperimentDetail => {
  const createdAt = new Date(
    Date.now() - 1000 * 60 * 60 * 24 * 28,
  ).toISOString();
  const updatedAt = new Date(Date.now() - 1000 * 60 * 60).toISOString();

  return {
    id: DEMO_EXPERIMENT_ID_CREDIT_CARD,
    name: DEMO_EXPERIMENT_NAME_CREDIT_CARD,
    goal:
      "Reduce credit card application completion time by 60% (from a baseline median of 22m 47s down to under 5 minutes) and increase the approval-to-activation conversion rate by 25 percentage points through a simplified, mobile-first digital application experience that compresses the legacy 12-step flow into a 5-step progressive flow with real-time document verification, bank-connected income population, and instant pre-qualification feedback. Success requires that the simplification preserves data quality, satisfies Reg Z disclosure obligations, and lifts the 14-day card activation rate alongside the headline completion metric — completion alone is not the win condition.",
    context:
      "Premium and prospective Chase cardholders experience significant friction during credit card application and upgrade flows. Our current 12-step process produces a 40% drop-off rate, with the steepest losses concentrated at the income verification and disclosure sections, and an average time-to-completion exceeding 25 minutes. Q3 conversion rates declined 11 points year-over-year on the Sapphire and Freedom acquisition funnels, and Voice-of-Customer surveys returned a 38 NPS for the application experience versus a 71 NPS for cardholder-stage interactions — confirming the application is the weakest segment of the journey. We are testing a streamlined 5-step flow with: (1) goal-based personalization, (2) bank-connected identity and income verification, (3) instant soft-pull pre-qualification, (4) collapsible plain-language terms, and (5) one-tap Apple Wallet activation. The pivot premise is that perceived complexity, not the underlying data collection, is the primary abandonment driver.",
    audience:
      "Premium and prospective Chase cardholders\nExisting Sapphire / Freedom / Ink customers seeking upgrades or additional cards\nNet-new applicants in the 24-55 age range with checking or savings relationships at Chase or competing banks\nMobile-first applicants (≥60% of acquisition traffic comes from mobile)\nUsers who have started — but not completed — a credit card application in the last 90 days",
    owner: "Sarah Chen",
    type: 2,
    status: "completed",
    createdAt,
    updatedAt,
    foundation: {
      objective:
        "Validate that a simplified 5-step credit card application flow with bank-connected verification will reduce abandonment by 40% and increase approval-to-activation conversion by 25 percentage points compared to the current 12-step process — without degrading data quality, regulatory compliance, or post-activation cardholder behavior. The experiment must produce decision-grade evidence on whether speed-of-completion is the right primary metric, or whether trust signals and perceived seriousness of the offer are equally consequential.",
      hypothesis:
        "If we reduce the credit card application steps from 12 to 5 with real-time document verification, bank-connected income population, and instant soft-pull offer preview, then completion rates will increase by at least 40% (from a baseline of 35%) and median completion time will drop below 5 minutes, because users abandon the legacy flow due to perceived complexity, excessive time investment, and uncertainty about whether they will be approved before they have to enter sensitive personal data.",
    },
    premises: [
      {
        id: "rq1",
        type: 1,
        value:
          "Will users trust the new auto-populated income verification from connected Chase bank accounts, and does the trust signal hold equally for net-new applicants without an existing Chase relationship?",
      },
      {
        id: "rq2",
        type: 1,
        value:
          "Does the simplified 5-step flow reduce cognitive load and time-to-completion as predicted, and do the gains hold uniformly across mobile and desktop devices and across age demographics?",
      },
      {
        id: "rq3",
        type: 1,
        value:
          "Can users complete the new streamlined flow in under 5 minutes with fewer inline-validation errors, and does meeting the speed target translate into higher 14-day card activation — or does fast completion correlate with weaker post-application commitment?",
      },
      {
        id: "a1",
        type: 2,
        value:
          "Users will trust auto-populated income data from connected Chase accounts without explicit attribution. (We expect this assumption to hold for existing Chase customers; we are deliberately exposing it to net-new applicants in this experiment to measure the trust delta.)",
      },
      {
        id: "a2",
        type: 2,
        value:
          "Reducing the application from 12 steps to 5 will not compromise data quality, fraud-detection signals, or Reg Z / Truth-in-Lending disclosure obligations. (Compliance team has pre-approved the proposed disclosure presentation; the experiment must not introduce new disclosure regressions.)",
      },
      {
        id: "a3",
        type: 2,
        value:
          "Mobile users will benefit equally — or more — from the streamlined desktop-first flow once mobile-specific UI affordances (44px tap targets, native input types, Apple Wallet integration) are layered in. (Mobile-first design was iterated on across three rounds of usability testing prior to launch.)",
      },
      {
        id: "s1",
        type: 3,
        value:
          "Users express confidence in the new process during post-task interviews; positive sentiment dominates open-ended feedback (>60% positive); users complete the flow without backtracking, restarting, or expressing confusion at any single step; existing Chase customers express that the connected-account verification feels respectful of their time rather than unsettling.",
      },
      {
        id: "s2",
        type: 3,
        value:
          "Form completion rate exceeds 60% (up from a 35% baseline); median completion time is under 5 minutes; inline-validation error rate falls below 5% (down from 11.4%); 14-day card activation rate among completers is at least 75% (above the 81% legacy benchmark would be a clean pursue; below 65% would indicate the speed gains are masking a trust regression and trigger a pivot recommendation); applicant NPS for the application experience increases by 15 or more points relative to the legacy control.",
      },
    ],
    okrs: [
      {
        id: "okr-c1",
        type: 1,
        okr: "Increase annual recurring revenue by 30% through product-led growth initiatives.",
      },
      {
        id: "okr-c2",
        type: 1,
        okr: "Reduce customer acquisition cost by 20% while maintaining conversion quality.",
      },
      {
        id: "okr-c3",
        type: 1,
        okr: "Achieve 95% customer satisfaction score and reduce churn by 15%.",
      },
      {
        id: "okr-t1",
        type: 2,
        okr: "Drive NPS >70 and increase premium user activation by delivering personalized, high-trust flows.",
      },
      {
        id: "okr-t2",
        type: 2,
        okr: "Reduce customer acquisition cost by 15% while improving conversion rates through optimized user experiences.",
      },
      {
        id: "okr-t3",
        type: 2,
        okr: "Increase monthly active users by 25% and improve user retention through enhanced product engagement.",
      },
    ],
    attachments: [],
  };
};

export const getCreditCardDemoFeedbackFlow = (): FeedbackFlow => {
  const route = (path: string) => ({ route: path });
  return {
    intro: {
      enabled: true,
      description:
        "Help Chase improve the credit card application experience. This 5-step prototype takes about 4 minutes. Your feedback will inform the next iteration of the application flow.",
      terms: null,
    },
    outro: {
      enabled: true,
      description:
        "Thanks for completing the prototype. Your responses are recorded — a member of the Chase team may reach out for a short follow-up interview.",
      terms: null,
    },
    demographicsEnabled: true,
    personalInfoEnabled: true,
    questions: [
      // Demographics
      {
        id: "demo-age",
        section: "demographics",
        source: "general",
        question: "What is your age range?",
        inputType: "multiple_choice",
        options: ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"],
        sortOrder: 1,
      },
      {
        id: "demo-income",
        section: "demographics",
        source: "general",
        question: "What is your annual household income?",
        inputType: "multiple_choice",
        options: [
          "Under $50k",
          "$50k-$75k",
          "$75k-$100k",
          "$100k-$150k",
          "$150k-$250k",
          "Over $250k",
        ],
        sortOrder: 2,
      },
      {
        id: "demo-relationship",
        section: "demographics",
        source: "general",
        question:
          "Do you currently have a Chase deposit or credit account?",
        inputType: "multiple_choice",
        options: [
          "Yes — checking or savings",
          "Yes — existing credit card",
          "Yes — both",
          "No — net new applicant",
        ],
        sortOrder: 3,
      },
      // Prototype questions — 4 per RQ, premiseId maps to research question IDs
      // RQ1 — auto-fill trust (Screen 2)
      {
        id: "iq-cc-trust-1",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq1",
        question:
          "When the form auto-filled your income from your Chase account, did that feel reassuring or unsettling?",
        inputType: "likert_scale",
        options: [
          "Very unsettling",
          "Somewhat unsettling",
          "Neutral",
          "Somewhat reassuring",
          "Very reassuring",
        ],
        taggedTarget: route("/verify"),
        sortOrder: 20,
      },
      {
        id: "iq-cc-trust-2",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq1",
        question:
          "Would you have preferred to enter your income manually, even if it took longer?",
        inputType: "multiple_choice",
        options: [
          "Yes — I prefer to enter it myself",
          "No — auto-fill is fine",
          "Only if Chase explained where it came from",
        ],
        taggedTarget: route("/verify"),
        sortOrder: 21,
      },
      {
        id: "iq-cc-trust-3",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq1",
        question:
          "How confident were you that the income shown was accurate and current?",
        inputType: "likert_scale",
        options: [
          "Not confident",
          "Slightly confident",
          "Moderately confident",
          "Very confident",
          "Completely confident",
        ],
        taggedTarget: route("/verify"),
        sortOrder: 22,
      },
      {
        id: "iq-cc-trust-4",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq1",
        question:
          "Did you feel Chase had clearly communicated where the auto-filled data came from?",
        inputType: "text",
        options: [],
        taggedTarget: route("/verify"),
        sortOrder: 23,
      },
      // RQ2 — cognitive load (Screens 1 + 4)
      {
        id: "iq-cc-load-1",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq2",
        question:
          "Compared to other credit-card applications you've completed, how mentally taxing was this one?",
        inputType: "likert_scale",
        options: [
          "Much more taxing",
          "Slightly more",
          "About the same",
          "Slightly easier",
          "Much easier",
        ],
        taggedTarget: route("/goal"),
        sortOrder: 10,
      },
      {
        id: "iq-cc-load-2",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq2",
        question:
          "On Screen 1 (Goal Selection), did the goal cards help you focus or get in your way?",
        inputType: "multiple_choice",
        options: [
          "Helped me focus",
          "Neutral",
          "Got in my way",
        ],
        taggedTarget: route("/goal"),
        sortOrder: 11,
      },
      {
        id: "iq-cc-load-3",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq2",
        question:
          "Were the collapsible Terms & Conditions sections clear, or did they feel like they were hiding something?",
        inputType: "text",
        options: [],
        taggedTarget: route("/terms"),
        sortOrder: 40,
      },
      {
        id: "iq-cc-load-4",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq2",
        question:
          "Did you feel the form asked you to make any decisions you weren't ready to make?",
        inputType: "multiple_choice",
        options: ["Yes", "No", "Unsure"],
        taggedTarget: route("/terms"),
        sortOrder: 41,
      },
      // RQ3 — under 5 min target (Screens 3 + 5)
      {
        id: "iq-cc-time-1",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq3",
        question: "Did this application feel rushed, well-paced, or slow?",
        inputType: "multiple_choice",
        options: ["Rushed", "Well-paced", "Slow"],
        taggedTarget: route("/done"),
        sortOrder: 50,
      },
      {
        id: "iq-cc-time-2",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq3",
        question:
          "When you saw the personalized offer on Screen 3, did the speed of getting there make you trust the offer more or less?",
        inputType: "likert_scale",
        options: [
          "Much less",
          "Less",
          "No effect",
          "More",
          "Much more",
        ],
        taggedTarget: route("/offer"),
        sortOrder: 30,
      },
      {
        id: "iq-cc-time-3",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq3",
        question:
          "On Screen 5 (Virtual Card Ready), did getting a card that quickly feel exciting or suspicious?",
        inputType: "text",
        options: [],
        taggedTarget: route("/done"),
        sortOrder: 51,
      },
      {
        id: "iq-cc-time-4",
        section: "prototype",
        source: "ai_generated",
        premiseId: "rq3",
        question:
          "If you finished the application but didn't activate the card, what was the reason?",
        inputType: "text",
        options: [],
        taggedTarget: route("/done"),
        sortOrder: 52,
      },
    ],
  };
};

export interface CreditCardDemoFinalizeOverrides {
  populationSize: number;
  targetResponses: number;
  currentResponses: number;
  avgCompletionTime: string;
  participantRows: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    referral: string;
    dateSubmitted: string;
  }>;
  demographicRows: Array<{
    id: string;
    question: string;
    leadingAnswer: string;
    completion: string;
  }>;
}

export const getCreditCardDemoFinalize =
  (): CreditCardDemoFinalizeOverrides => ({
    populationSize: 70,
    targetResponses: 60,
    currentResponses: 44,
    avgCompletionTime: "4m 11s",
    participantRows: [
      {
        id: 1,
        firstName: "David",
        lastName: "Lin",
        email: "david.lin@gmail.com",
        referral: "Chase customer outreach",
        dateSubmitted: "04/14/26 10:12 AM",
      },
      {
        id: 2,
        firstName: "Carla",
        lastName: "Reyes",
        email: "carla.reyes@outlook.com",
        referral: "Premium-tier panel",
        dateSubmitted: "04/14/26 11:38 AM",
      },
      {
        id: 3,
        firstName: "Jamal",
        lastName: "Ferguson",
        email: "j.ferguson@gmail.com",
        referral: "Chase customer outreach",
        dateSubmitted: "04/14/26 1:02 PM",
      },
      {
        id: 4,
        firstName: "Emily",
        lastName: "Walsh",
        email: "emily.walsh@yahoo.com",
        referral: "Premium-tier panel",
        dateSubmitted: "04/14/26 2:47 PM",
      },
      {
        id: 5,
        firstName: "Marcus",
        lastName: "Doyle",
        email: "marcus.doyle@gmail.com",
        referral: "Chase customer outreach",
        dateSubmitted: "04/14/26 4:21 PM",
      },
      {
        id: 6,
        firstName: "Naomi",
        lastName: "Park",
        email: "naomi.park@hotmail.com",
        referral: "Net-new applicant pool",
        dateSubmitted: "04/15/26 9:08 AM",
      },
      {
        id: 7,
        firstName: "Greg",
        lastName: "Sanchez",
        email: "g.sanchez@gmail.com",
        referral: "Chase customer outreach",
        dateSubmitted: "04/15/26 10:55 AM",
      },
      {
        id: 8,
        firstName: "Pamela",
        lastName: "O'Connor",
        email: "p.oconnor@outlook.com",
        referral: "Premium-tier panel",
        dateSubmitted: "04/15/26 12:33 PM",
      },
    ],
    demographicRows: [
      {
        id: "age-range",
        question: "What is your age range?",
        leadingAnswer: "35-44",
        completion: "44/44 completed",
      },
      {
        id: "income",
        question: "What is your annual household income?",
        leadingAnswer: "$100k-$150k",
        completion: "44/44 completed",
      },
      {
        id: "relationship",
        question:
          "Do you currently have a Chase deposit or credit account?",
        leadingAnswer: "Yes — both",
        completion: "44/44 completed",
      },
    ],
  });

export const getCreditCardDemoPrototype = (): PrototypeDetailResponse => {
  const createdDate = new Date(
    Date.now() - 1000 * 60 * 60 * 24 * 24,
  ).toISOString();
  const completedDate = new Date(
    Date.now() - 1000 * 60 * 60 * 24 * 23,
  ).toISOString();
  const deployedDate = new Date(
    Date.now() - 1000 * 60 * 60 * 24 * 23,
  ).toISOString();

  const demoUrl = resolveDemoPrototypeUrl();

  return {
    id: "proto-cc-3",
    iterationId: "iter-cc-3",
    experimentId: DEMO_EXPERIMENT_ID_CREDIT_CARD,
    summaryPlan:
      "Chase-branded 5-step credit card application prototype. Step 1: goal-based card matching. Step 2: identity & bank-connected verification with attribution. Step 3: instant soft-pull personalized offer with 'why this offer?' rationale. Step 4: plain-language collapsible terms. Step 5: virtual card reveal with one-tap Apple Wallet add. Mobile-first responsive layout, Chase blue (#117ACA) on white, Inter typography, lucide iconography. Implements all interview-tagged interaction points used during the validation study.",
    status: 2,
    prototypeType: "v0",
    v0ChatId: "v0-cc-3-chat",
    v0WebUrl: demoUrl,
    v0DemoUrl: demoUrl,
    currentVersionId: "ver-cc-3-current",
    errorMessage: null,
    createdDate,
    completedDate,
    deploymentId: "dep-cc-3",
    deploymentStatus: 2,
    publicUrl: demoUrl,
    blobUrl: demoUrl,
    deploymentErrorMessage: null,
    deployedDate,
    deploymentProgress: 100,
    versions: [
      {
        id: "ver-cc-3-current",
        iterationId: "iter-cc-3",
        chatId: "v0-cc-3-chat",
        versionNumber: 1,
        status: 2,
        v0VersionId: "v0-version-cc-3",
        v0DemoUrl: demoUrl,
        v0ScreenshotUrl: undefined,
        isCurrentVersion: true,
        createdDate,
        completedDate,
      },
    ],
  };
};
